import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, TableSkeleton, RowActions, RowAction,
  ListPagination, ListSearchInput, ListEmptyRow, StatusDot, Amount, btnPrimary,
} from '@redpanda/ui';
import { isAdmin, useRefetchOnFocus, useSyncUrlPage, useUrlPage, useUrlSearch } from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import BankAccountDialog from '../components/BankAccountDialog';
import { BankAccount, useBankAccountStoreBase, useBankSummary } from '../stores/bankAccountStore';
import { bankAccountService } from '../services/api';
import { useListSearch } from '../hooks/useListSearch';
import { notifyDataChanged, useDataChanged } from '../utils/dataEvents';
import { pendingRowClass } from '../utils/pendingItem';

const COLUMNS = 6;

/**
 * 銀行帳戶：點帳戶名稱進交易明細（D9）；餘額＝期初＋交易，由後端計算
 * 原本 3 張統計卡（總帳戶數、啟用帳戶、總餘額）改成一行摘要
 */
const BankAccountPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext)!;
  const admin = isAdmin(user?.role);

  const store = useBankAccountStoreBase();
  const { items: accounts, pagination, isLoading, isPageLoading, showDeleteConfirmModal, isDeleting, fetchItems } = store;
  const summary = useBankSummary({ all: true });
  const [dialogAccount, setDialogAccount] = useState<BankAccount | null | undefined>(undefined);

  // 網址 ?page=、?q=：從交易明細返回時回到同一頁、保留搜尋（R50）
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = { pathname: location.pathname, navigationType: useNavigationType(), searchParams, setSearchParams };
  const initialPage = useUrlPage(urlPage);
  const [searchTerm, setSearchTerm] = useUrlSearch(urlPage);

  useEffect(() => {
    fetchItems(initialPage);
    summary.refetch();
  }, [fetchItems]); // eslint-disable-line react-hooks/exhaustive-deps
  const refreshAll = () => {
    useBankAccountStoreBase.getState().refreshData();
    summary.refetch();
  };
  useRefetchOnFocus(refreshAll);
  useDataChanged(['bankAccounts', 'bankTransactions'], refreshAll);

  const search = searchTerm.trim();
  const searchResult = useListSearch<BankAccount>('bankAccounts', search ? { search } : null, page => bankAccountService.getBankAccounts(page, 20, search), initialPage);
  const listItems = searchResult.active ? searchResult.items : accounts;
  const listPagination = searchResult.active ? searchResult.pagination : pagination;
  const listLoading = searchResult.active ? searchResult.isLoading : isPageLoading;
  const changePage = (page: number) => (searchResult.active ? searchResult.setPage(page) : fetchItems(page));
  useSyncUrlPage(urlPage, listPagination, changePage);

  const handleConfirmDelete = async () => {
    await store.confirmDelete();
    notifyDataChanged('bankAccounts');
  };

  const addButton = (
    <button type="button" onClick={() => setDialogAccount(null)} className={btnPrimary}>
      {t('bankAccounts.list.addButton')}
    </button>
  );

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex flex-wrap items-baseline justify-between gap-y-3">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('bankAccounts.title')}</h1>
          <div className="flex space-x-3 whitespace-nowrap">{addButton}</div>
        </div>
      </div>

      <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="md:w-80">
          <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('bankAccounts.list.searchPlaceholder')} />
        </div>
        {summary.data && (
          <p className="text-sm text-fg-secondary md:ml-auto tabular-nums">
            {t('bankAccounts.list.summary', { count: summary.data.activeAccounts })}
            <span className="mx-2 text-fg-muted">·</span>
            {t('bankAccounts.list.totalBalance')} <span className="font-medium text-fg"><Amount value={summary.data.totalBalance} /></span>
          </p>
        )}
      </div>

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mb-3" />

      {(isLoading && accounts.length === 0 && !searchResult.active) || listLoading ? (
        <TableSkeleton rows={8} columns={COLUMNS} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '900px' }}>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('bankAccounts.table.account')}</TableHead>
                  <TableHead className="w-40">{t('bankAccounts.table.accountNumber')}</TableHead>
                  <TableHead className="w-28">{t('bankAccounts.table.type')}</TableHead>
                  <TableHead numeric className="w-44">{t('bankAccounts.table.balance')}</TableHead>
                  <TableHead numeric className="w-28">{t('bankAccounts.table.unreconciled')}</TableHead>
                  <TableHead className="w-32">{t('bankAccounts.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listItems.length === 0 ? (
                  <ListEmptyRow
                    colSpan={COLUMNS}
                    filtered={!!search}
                    title={t('bankAccounts.list.empty')}
                    hint={t('bankAccounts.list.emptyHint')}
                    action={addButton}
                  />
                ) : (
                  listItems.map(account => {
                    const hasTransactions = (account._count?.transactions ?? 0) > 0;
                    return (
                      <TableRow key={account.id} className={pendingRowClass(account)}>
                        <TableCell className="truncate">
                          <Link to={`/bank-accounts/${account.id}`} className="font-medium text-fg hover:text-accent transition-colors">
                            {account.accountName}
                          </Link>
                          <span className="block text-[13px] text-fg-secondary truncate">
                            {account.bankName}
                            {!account.isActive && <> · <StatusDot tone="neutral" label={t('bankAccounts.status.inactive')} /></>}
                          </span>
                        </TableCell>
                        <TableCell className="truncate tabular-nums" title={account.accountNumber}>{account.accountNumber}</TableCell>
                        <TableCell>{t(`bankAccounts.types.${account.accountType}`)}</TableCell>
                        <TableCell numeric>
                          <Amount value={Number(account.currentBalance)} currency={account.currency !== 'TWD' ? `${account.currency} ` : undefined} />
                        </TableCell>
                        <TableCell numeric>
                          {account.unreconciledCount ? account.unreconciledCount : <span className="text-fg-muted">—</span>}
                        </TableCell>
                        <TableCell>
                          <RowActions>
                            <RowAction onClick={() => setDialogAccount(account)}>{t('common.actions.edit')}</RowAction>
                            {admin && (
                              <RowAction
                                tone="danger"
                                disabled={hasTransactions}
                                title={hasTransactions ? t('errors.BANK_ACCOUNT_HAS_TRANSACTIONS') : undefined}
                                onClick={() => store.handleDelete(account.id)}
                              >
                                {t('common.actions.delete')}
                              </RowAction>
                            )}
                          </RowActions>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mt-6" />

      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={store.closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title={t('bankAccounts.list.confirmDeleteTitle')}
        message={t('bankAccounts.list.confirmDeleteMessage')}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      {dialogAccount !== undefined && <BankAccountDialog account={dialogAccount ?? undefined} onClose={() => setDialogAccount(undefined)} />}
    </div>
  );
};

export default BankAccountPage;
